import { useState } from 'react'
import type { TeamRow } from '../../pages/admin/teamStore'

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

export type EditTeamValues = {
  name: string
  openDate: string
  // Null leaves the team open-ended, which reopens a closed one.
  closeDate: string | null
}

/**
 * A team's name and its open and close dates. Clearing the close date reopens
 * a closed team. The server refuses a team that would open after employees
 * were placed on it, or close while employees are still placed on it.
 */
export default function EditTeamForm({
  team,
  saving,
  error,
  onSave,
  onCancel,
}: {
  team: TeamRow
  saving: boolean
  error: string | null
  onSave: (values: EditTeamValues) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(team.name)
  const [openDate, setOpenDate] = useState(team.openDate)
  const [closeDate, setCloseDate] = useState(team.closeDate ?? '')

  const changed = name.trim() !== team.name || openDate !== team.openDate || closeDate !== (team.closeDate ?? '')
  const canSave = name.trim() !== '' && openDate !== '' && (closeDate === '' || closeDate >= openDate) && changed

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label={`Edit ${team.name}`}
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave && !saving) onSave({ name: name.trim(), openDate, closeDate: closeDate || null })
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor="edit-team-name" className={LABEL}>
            Team name
          </label>
          <input id="edit-team-name" type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="edit-team-open-date" className={LABEL}>
            Opens
          </label>
          <input id="edit-team-open-date" type="date" value={openDate} onChange={(event) => setOpenDate(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="edit-team-close-date" className={LABEL}>
            Closes (optional)
          </label>
          <input
            id="edit-team-close-date"
            type="date"
            min={openDate || undefined}
            aria-describedby="edit-team-close-hint"
            value={closeDate}
            onChange={(event) => setCloseDate(event.target.value)}
            className={FIELD}
          />
          <p id="edit-team-close-hint" className="mt-1 text-caption text-ink-subtle">
            {team.closeDate ? 'Clear the date to reopen the team.' : 'Set for a temporary team.'}
          </p>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
